import { useEffect, useMemo, useRef, useState } from 'react';
import { useGenerateImageMutation } from '@circuitry-ai/doc-data';
import { checkType, CtyShowToast } from '@circuitry-ai/doc-ui';
import ExplainerTable from './explainerTable';

/** Dedupes concurrent/repeated presign requests for the same path set (e.g. Strict Mode or tab remounts). */
const imagePresignPromises = new Map<string, Promise<Record<string, string>>>();

const ExplainerImages = ({
  imageEntries,
  details,
  advisor_id,
}: {
  imageEntries: any[];
  details: any;
  advisor_id?: string;
}) => {
  const [generateImage] = useGenerateImageMutation();
  const generateImageRef = useRef(generateImage);
  generateImageRef.current = generateImage;
  const [presignedByPath, setPresignedByPath] = useState<Record<string, string>>({});
  const [urlsLoading, setUrlsLoading] = useState(true);

  const pathsKey = useMemo(
    () =>
      imageEntries
        .map((e) => e?.img_path)
        .filter((p: string) => p && String(p).trim())
        .join('\0'),
    [imageEntries]
  );

  useEffect(() => {
    const paths = pathsKey
      ? pathsKey.split('\0').filter((p) => p && String(p).trim())
      : [];
    if (paths.length === 0) {
      setPresignedByPath({});
      setUrlsLoading(false);
      return;
    }
    let cancelled = false;
    setUrlsLoading(true);

    let presignPromise = imagePresignPromises.get(pathsKey);
    if (!presignPromise) {
      presignPromise = generateImageRef
        .current({ s3_paths: paths })
        .unwrap()
        .then((res) => {
          const map: Record<string, string> = {};
          (res?.data ?? []).forEach((item: { s3_path: string; presigned_url: string }) => {
            if (item?.s3_path && item?.presigned_url) map[item.s3_path] = item.presigned_url;
          });
          return map;
        })
        .catch((e) => {
          imagePresignPromises.delete(pathsKey);
          throw e;
        });
      imagePresignPromises.set(pathsKey, presignPromise);
    }

    presignPromise
      .then((map) => {
        if (!cancelled) {
          setPresignedByPath(map);
          setUrlsLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          CtyShowToast(checkType.error, (e as Error)?.message || 'Failed to load images');
          setPresignedByPath({});
          setUrlsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pathsKey]);

  const rowData = useMemo(
    () =>
      imageEntries.map((entry) => {
        const pl = entry?.page_label;
        const page_labels =
          pl !== undefined && pl !== null && String(pl).trim() !== ''
            ? [Number(pl)]
            : [];
        return {
          ...entry,
          file_name: entry?.file_name ?? entry?.source_name,
          page_labels,
          presigned_url: entry?.img_path ? presignedByPath[entry.img_path] : undefined,
          _urlsResolved: !urlsLoading,
        };
      }),
    [imageEntries, presignedByPath, urlsLoading]
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="font-medium text-lg">Image context</div>
          {details?.time_taken_image_retriever != null && details?.time_taken_image_retriever !== '' && (
            <div className="text-sm">
              <span className="font-medium">Time Taken:</span> {details.time_taken_image_retriever} S
            </div>
          )}
        </div>
        <div className="flex w-full flex-row items-center justify-between mt-1">
          <div className="text-sm font-normal leading-snug">
            Images retrieved and ranked as context for this answer.
          </div>
          {details?.llm_model_image_retriever != null && details?.llm_model_image_retriever !== '' && (
            <div className="text-sm">
              <span className="font-medium">LLM Model:</span> {details.llm_model_image_retriever}
            </div>
          )}
        </div>
      </div>
      <ExplainerTable rowData={rowData} advisor_id={advisor_id} tab="images" />
    </div>
  );
};

export default ExplainerImages;
